import React from "react";

import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";

import FieldContainer from "./FieldContainer";
import Label from "./Label";
import { wrapperClasses } from "./styles";

import type { ChangeEvent, JSX } from "react";

import type { InputProps } from "./types";

type SwitchUsingFormControlProps = InputProps & {
  labelClassNames?: string;
  color?: string;
  onChangeCustom?: (value: ChangeEvent<HTMLInputElement>) => void;
};

export default function SwitchUsingFormControl({
  name,
  label,
  sublabel,
  infoText,
  isRequired = false,
  isReadOnly = false,
  defaultValue,
  isCompact,
  className,
  isNoWrap,
  control,
  labelClassNames,
  color,
  onChangeCustom,
  isReversed,
}: SwitchUsingFormControlProps): JSX.Element {
  const { t } = useTranslation("editor-errors");

  return (
    <Controller
      control={control}
      defaultValue={defaultValue}
      name={name}
      render={({
        field: { onChange, onBlur, value },
        fieldState: { error },
      }): JSX.Element => (
        <FieldContainer
          isCentered
          className={className}
          isCompact={isCompact}
          isNoWrap={isNoWrap}
          isReversed={isReversed}
        >
          {label ? (
            <Label
              isCompact={isCompact}
              isRequired={isRequired}
              isStartLabel={false}
              label={label}
              name={name}
              sublabel={sublabel}
            />
          ) : null}
          <div className={wrapperClasses({ isCompact, isReversed })}>
            <div className="flex items-center gap-[0.78125rem]">
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label
                className={labelClassNames}
                htmlFor={name}
                style={{
                  backgroundColor: value && color ? color : "",
                }}
              >
                <input
                  checked={value}
                  className="invisible size-0"
                  defaultValue={defaultValue}
                  disabled={isReadOnly}
                  id={name}
                  onBlur={onBlur}
                  type="checkbox"
                  value={value}
                  onChange={(e): void => {
                    onChange(e);
                    onChangeCustom?.(e);
                  }}
                />
              </label>
              {infoText}
            </div>
          </div>
          {error ? (
            <span className="text-destructive text-[0.9375rem] whitespace-nowrap">
              {error.message}
            </span>
          ) : null}
        </FieldContainer>
      )}
      rules={{
        required: isRequired && t("requiredField"),
      }}
    />
  );
}
